import { useState } from 'react';
import { Check, X, Zap, Globe, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PayPalButtons } from '@paypal/react-paypal-js';
import Header from '../../../components/marketplace/Header';
const BASE_URL = process.env.REACT_APP_BASE_URL;

const IndustryPayment = () => {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [paypalPlan, setPaypalPlan] = useState(null);
  const plans = {
    monthly: {
      name: 'Monthly',
      description: 'Perfect for individuals who want access to all core features at an affordable price.',
      price: 10,
      plan_id: process.env.REACT_APP_PAYPAL_Industryy_BASIC_PLAN_ID,
   features: [
  { included: true, text: 'Full access to all industry management tools' },
  { included: true, text: 'Unlimited projects & departments' },
  { included: true, text: 'Employee & team analytics & reporting' },
  { included: true, text: '50 GB storage for company resources' },
  { included: true, text: 'Standard security & regular updates' },
  { included: true, text: 'Full team collaboration (employees & managers)' },
  { included: true, text: 'Priority email & chat support' },
],


      icon: Sparkles,
    },
    quarterly: {
      name: 'Six Months',
      description: 'Ideal for individuals who want reliable performance and full-feature access for their work.',
      price: 60,
       plan_id: process.env.REACT_APP_PAYPAL_Industry_ADVANCED_PLAN_ID,
    features: [
  { included: true, text: 'Full access to all industry management tools' },
  { included: true, text: 'Unlimited projects & departments' },
  { included: true, text: 'Employee & team analytics & reporting' },
  { included: true, text: '50 GB storage for company resources' },
  { included: true, text: 'Standard security & regular updates' },
  { included: true, text: 'Full team collaboration (employees & managers)' },
  { included: true, text: 'Priority email & chat support' },
],


      icon: Zap,
      popular: true,
    },
    yearly: {
      name: 'Yearly',
      description: 'For individuals seeking the complete experience with all features unlocked.',
      price: 120,
       plan_id: process.env.REACT_APP_PAYPAL_Industry_PRO_PLAN_ID,
    features: [
  { included: true, text: 'Full access to all industry management tools' },
  { included: true, text: 'Unlimited projects & departments' },
  { included: true, text: 'Employee & team analytics & reporting' },
  { included: true, text: '50 GB storage for company resources' },
  { included: true, text: 'Standard security & regular updates' },
  { included: true, text: 'Full team collaboration (employees & managers)' },
  { included: true, text: 'Priority email & chat support' },
],


      icon: Globe,
    },
  };

  const handleSubscribe = (planKey) => {
    const plan = plans[planKey];
    setPaypalPlan(plan);
  };

  const handleSubscriptionSuccess = async (subscriptionID) => {
    try {
      const res = await fetch(`${BASE_URL}/paypal/industry-payment/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'x-api-key': process.env.REACT_APP_X_API_KEY,
        },
        body: JSON.stringify({ subscription_id: subscriptionID }),
      });
      if (!res.ok) throw new Error('Failed to update subscription on backend');
      alert('Subscription completed successfully!');
      setPaypalPlan(null);
      navigate('/marketplace/industry');
    } catch (err) {
      console.error(err);
      alert('Subscription completed but backend update failed.');
    }
  };

  const handlePaymentCancel = () => {
    alert('Payment cancelled');
    setPaypalPlan(null);
  };

  return (
         <>
       <Header/>
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4  py-12">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#e8f0f7] rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-[#0f6abf]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-semibold text-gray-900">Choose Your Perfect Industry Plan</h1>
            </div>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-16">
          {Object.entries(plans).map(([key, plan]) => {
            const Icon = plan.icon;
            const isSelected = selectedPlan === key;

            return (
              <div
                key={key}
                className={`relative rounded-2xl border transition-all duration-200 ${
                  isSelected 
                    ? 'border-[#0f6abf] bg-white shadow-lg' 
                    : 'border-gray-200 bg-white hover:border-gray-300'
                } ${plan.popular ? 'ring-2 ring-[#0f6abf]' : ''}`}
                onClick={() => setSelectedPlan(key)}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-[#0f6abf] text-white text-xs font-medium px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-[#e8f0f7] rounded-lg flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#0f6abf]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
                      <p className="text-sm text-gray-500">{plan.description}</p>
                    </div>
                  </div>

                  <div className="mb-6">
                    <div className="flex items-baseline">
                      <span className="text-3xl font-bold text-gray-900">${plan.price}</span>
                      <span className="text-gray-500 ml-2">/{plan.name.toLowerCase()}</span>
                    </div>
                  </div>

                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        {feature.included ? (
                          <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <Check className="w-3 h-3 text-green-600" />
                          </div>
                        ) : (
                          <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <X className="w-3 h-3 text-gray-400" />
                          </div>
                        )}
                        <span className={`text-sm ${feature.included ? 'text-gray-700' : 'text-gray-400'}`}>
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isSelected) setSelectedPlan(key);
                      else handleSubscribe(key);
                    }}
                    className={`w-full py-3 px-4 rounded-xl font-medium transition-colors ${
                      isSelected
                        ? 'bg-[#0f6abf] text-white hover:bg-[#0a5ca8]'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {isSelected ? 'Buy Now' : 'Select Plan'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* PayPal Subscription Modal */}
        {paypalPlan && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-8 w-full max-w-md">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#e8f0f7] rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-[#0f6abf]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Complete Subscription</h2>
                  <p className="text-sm text-gray-500">For {paypalPlan.name} plan</p>
                </div>
              </div>
              
              <PayPalButtons
                style={{ layout: 'vertical' }}
                createSubscription={(data, actions) => {
                  return actions.subscription.create({
                    plan_id: paypalPlan.plan_id
                  });
                }}
                onApprove={(data) => {
                  console.log("approve data is:",data)
                  handleSubscriptionSuccess(data.subscriptionID);
                }}
                onCancel={handlePaymentCancel}
                onError={(err) => {
                  console.error('PayPal subscription error:', err);
                  handlePaymentCancel();
                }}
              />
              
              <button
                onClick={handlePaymentCancel}
                className="w-full mt-4 py-3 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default IndustryPayment;